<script setup>
import { ref, nextTick, watch } from 'vue';
import { useChatStore } from '../../stores/chat';

const chatStore = useChatStore();
const messagesContainer = ref(null);

// Auto scroll to bottom when new messages arrive
const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

// Watch for message changes
watch(
  () => [chatStore.messages.length, chatStore.isSending],
  () => {
    scrollToBottom();
  }
);
</script>

<template>
  <div
    ref="messagesContainer"
    class="dataagent-ai-messages da:max-h-[calc(100vh-15rem)] da:overflow-y-auto da:p-4 da:space-y-4"
  >
    <!-- Default Welcome Message -->
    <div
      v-if="chatStore.messages.length === 0"
      class="dataagent-welcome-section da:flex da:flex-col da:items-center da:justify-center da:text-center da:py-12 da:px-6"
    >
      <!-- Logo -->
      <img :src="chatStore.settings.logo" alt="DataAgent" class="dataagent-animate-wiggle da:w-40 da:h-40 da:pb-4" />

      <!-- Welcome Title -->
      <h2 class="da:text-3xl md:da:text-4xl da:font-bold da:text-gray-800 da:mb-4 da:leading-tight">
        {{ chatStore.labels.welcome_title || 'Welcome to DataAgent' }}
      </h2>

      <!-- Welcome Description -->
      <p class="da:text-base md:da:text-lg da:text-gray-600 da:max-w-2xl da:leading-relaxed">
        {{
          chatStore.labels.welcome_message ||
          'Get started by selecting a task and DataAgent can do the rest. Not sure where to start?'
        }}
      </p>
    </div>

    <!-- Message List -->
    <template v-for="message in chatStore.messages" :key="message.id">
      <!-- User Message -->
      <div
        v-if="message.type === 'user'"
        class="dataagent-ai-message dataagent-user-message da:flex da:items-start da:space-x-3 da:justify-end"
      >
        <div
          class="dataagent-ai-content dataagent-user-content da:bg-green-500 da:text-white da:p-3 da:rounded-lg da:shadow-sm da:max-w-md"
          v-html="message.content"
        ></div>
        <div
          class="dataagent-ai-avatar dataagent-user-avatar da:bg-gradient-to-br da:from-green-500 da:to-green-600 da:text-white da:p-2 da:rounded-full da:flex da:items-center da:justify-center da:flex-shrink-0 da:ring-2 da:ring-green-200"
        >
          <span class="dashicons dashicons-admin-users da:text-lg"></span>
        </div>
      </div>

      <!-- AI Message -->
      <div v-else class="dataagent-ai-message da:flex da:items-start da:space-x-3">
        <div
          class="dataagent-ai-avatar da:bg-gradient-to-br da:from-green-300 da:to-green-500 da:p-1 da:rounded-full da:flex da:items-center da:justify-center da:flex-shrink-0 da:ring-2 da:ring-green-200"
        >
          <img
            :src="chatStore.settings.logo"
            alt="DataAgent AI"
            class="da:w-8 da:h-8 da:rounded-full da:object-cover"
          />
        </div>
        <div
          class="dataagent-ai-content da:bg-white da:p-3 da:rounded-lg da:shadow-sm da:border da:border-gray-200 da:max-w-3xl"
          :class="{ 'da:bg-red-50 da:border-red-200 da:text-red-800': message.isError }"
          v-html="message.content"
        ></div>
      </div>
    </template>

    <!-- Loading Message -->
    <div
      v-if="chatStore.isSending"
      class="dataagent-ai-message dataagent-ai-loading da:flex da:items-start da:space-x-3"
    >
      <div
        class="dataagent-ai-avatar da:bg-gradient-to-br da:from-green-300 da:to-green-500 da:p-1 da:rounded-full da:flex da:items-center da:justify-center da:flex-shrink-0 da:ring-2 da:ring-green-200 da:animate-pulse"
      >
        <img :src="chatStore.settings.logo" alt="DataAgent AI" class="da:w-8 da:h-8 da:rounded-full da:object-cover" />
      </div>
      <div class="dataagent-ai-content da:bg-white da:p-3 da:rounded-lg da:shadow-sm da:border da:border-gray-200">
        <span class="dataagent-loading-dots da:flex da:space-x-1">
          <span
            class="da:w-2 da:h-2 da:bg-gray-500 da:rounded-full da:animate-bounce"
            style="animation-delay: 0ms"
          ></span>
          <span
            class="da:w-2 da:h-2 da:bg-gray-500 da:rounded-full da:animate-bounce"
            style="animation-delay: 150ms"
          ></span>
          <span
            class="da:w-2 da:h-2 da:bg-gray-500 da:rounded-full da:animate-bounce"
            style="animation-delay: 300ms"
          ></span>
        </span>
      </div>
    </div>
  </div>
</template>
